import React from "react";
import { type NextPage } from "next";
import { Text } from "@chakra-ui/react";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import { sectionSpacingY } from "components/section";
import { DestinationMarquee } from "components/content";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import {
  EventCollectionHero,
  DataCollectionFeatures,
  DataActivationSteps,
  EventStreaming,
  WorkflowFeatures,
} from "components/platform/event-collection";
import { AuraCardQuote } from "components/quotes";
import { integrationsCount } from "../../constants/integrations";

const EventCollection: NextPage<LandingPageProps> = ({
  page,
  announcementBanner,
  destinations,
  defaultLogos,
  customers,
}) => {
  const morningbrew = getSafeCustomer(customers, "morning-brew");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor="new.gray.950"
    >
      <EventCollectionHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos
        title=""
        customers={defaultLogos}
        theme="dark"
        themeBgColor="new.gray.950"
        bg="new.gray.950"
        py="0"
        pb={sectionSpacingY}
      />

      <DataActivationSteps />

      <DataCollectionFeatures />

      <EventStreaming />

      <WorkflowFeatures />

      <AuraCardQuote
        my="0"
        py={sectionSpacingY}
        quote={{
          author: {
            name: "Erika Olsen",
            job: "Senior Growth Marketing Manager",
          },
          company: {
            name: "Morning Brew",
            logo: morningbrew.logoUrl,
            logoRatio: 173 / 30,
          },
          quoteText: (
            <>
              With Hightouch, we can use any and all of our data across any channel to serve more{" "}
              <Text as="span" fontWeight="bold">
                personalized and relevant experiences
              </Text>{" "}
              to our subscribers.
            </>
          ),
          cta: { text: "Read case study", href: "/customers/morning-brew" },
        }}
      />

      <DestinationMarquee
        title={`Sync data to ${integrationsCount}+ tools`}
        destinations={destinations}
        my={0}
        pb={sectionSpacingY}
      />
    </Page>
  );
};

export default EventCollection;

export const getStaticProps = getLandingPageProps("/platform/event-collection", {
  loadDestinations: "all",
  loadDefaultLogos: true,
  loadCustomers: ["morning-brew"],
});
